import { createContext, useContext, useLayoutEffect, useMemo, type ReactNode } from "react";
import { useLocalStorage, useMediaQuery } from "./hooks";

export type ThemePreference = "light" | "dark" | "system";
export type SidebarStyle = "light" | "navy";

interface ThemeContextValue {
  theme: ThemePreference;
  resolvedTheme: "light" | "dark";
  setTheme: (t: ThemePreference) => void;
  sidebarStyle: SidebarStyle;
  setSidebarStyle: (s: SidebarStyle) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

/**
 * Light / dark / system theme. Applied as `.dark` + `data-theme` on <html>
 * (index.html has a matching no-flash boot script). Sidebar can be the light
 * cream variant (default) or the brand navy variant.
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useLocalStorage<ThemePreference>("theme", "system");
  const [sidebarStyle, setSidebarStyle] = useLocalStorage<SidebarStyle>("sidebar-style", "light");
  const prefersDark = useMediaQuery("(prefers-color-scheme: dark)");
  const resolvedTheme = theme === "system" ? (prefersDark ? "dark" : "light") : theme;

  useLayoutEffect(() => {
    const root = document.documentElement;
    root.classList.toggle("dark", resolvedTheme === "dark");
    root.setAttribute("data-theme", resolvedTheme);
    root.style.colorScheme = resolvedTheme;
    root.classList.toggle("sidebar-navy", sidebarStyle === "navy");
  }, [resolvedTheme, sidebarStyle]);

  const value = useMemo(
    () => ({ theme, resolvedTheme, setTheme, sidebarStyle, setSidebarStyle }) satisfies ThemeContextValue,
    [theme, resolvedTheme, setTheme, sidebarStyle, setSidebarStyle],
  );
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used inside <ThemeProvider>");
  return ctx;
}
